import { useQuery } from '@tanstack/react-query';
import { UzConfirmDialog, UzSpinner } from '@utazzo/ui';
import React from 'react';

import { getExitReleasePreview } from '@/features/leads/leads.api';

/**
 * Which lifecycle action is being confirmed. Each maps to a distinct
 * title / verb / confirm label but shares the same release-preview copy.
 */
export type ExitReleaseAction = 'deactivate' | 'delete' | 'date_of_exit';

interface ExitReleaseConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The departing user. `null` while the dialog is closed / unmounted. */
  userId: string | null;
  userName: string;
  action: ExitReleaseAction;
  /** Fires the underlying mutation. Awaited before the dialog closes. */
  onConfirm: () => Promise<void>;
}

const COPY: Record<
  ExitReleaseAction,
  { title: (name: string) => string; confirmLabel: string; verb: string }
> = {
  deactivate: {
    title: (name) => `Deactivate ${name}?`,
    confirmLabel: 'Deactivate',
    verb: 'deactivated',
  },
  delete: {
    title: (name) => `Delete ${name}?`,
    confirmLabel: 'Delete user',
    verb: 'soft-deleted',
  },
  date_of_exit: {
    title: (name) => `Set exit date for ${name}?`,
    confirmLabel: 'Save & release',
    verb: 'marked as exited',
  },
};

/**
 * Confirmation modal for the three lifecycle actions that release a user's
 * leads into the exit-unassigned queue (deactivate / delete / date-of-exit
 * reaching today). Fetches a live count of how many Active + Not Qualified
 * leads will move, and spells out that the reporting manager and any admin
 * inherit them. Fails open: if the preview call errors, we still let the
 * operator proceed with a soft warning (the release itself is idempotent
 * and enforced server-side regardless of what this preview showed).
 */
export const ExitReleaseConfirmDialog = ({
  open,
  onOpenChange,
  userId,
  userName,
  action,
  onConfirm,
}: ExitReleaseConfirmDialogProps): React.JSX.Element => {
  const preview = useQuery({
    queryKey: ['exit-release-preview', userId],
    queryFn: () => getExitReleasePreview(userId as string),
    enabled: open && Boolean(userId),
    staleTime: 30_000,
  });

  const copy = COPY[action];
  const total = preview.data?.total ?? 0;
  const activeStage = preview.data?.breakdown.activeStage ?? 0;
  const notQualified = preview.data?.breakdown.notQualified ?? 0;

  const description = (
    <span className="block space-y-2">
      <span className="block">
        {userName} will be {copy.verb} and lose access immediately.
      </span>
      {preview.isLoading ? (
        <span className="flex items-center gap-2 text-zinc-500">
          <UzSpinner className="h-4 w-4" />
          Counting the leads that will move…
        </span>
      ) : preview.isError ? (
        <span className="block rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
          Couldn't load the exact count right now. Any Active (Proposal / Hot / Warm) and Not
          Qualified leads currently assigned to them will still move to the Unassigned queue,
          visible to their reporting manager and any admin.
        </span>
      ) : total > 0 ? (
        <span className="block rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-zinc-700">
          <span className="block font-medium text-zinc-900">
            {total} lead{total === 1 ? '' : 's'} will move to the Unassigned queue
          </span>
          <span className="block text-sm">
            {activeStage} Active (Proposal / Hot / Warm) · {notQualified} Not Qualified. These
            become visible to their reporting manager and any admin, who can reassign them to an
            active agent.
          </span>
        </span>
      ) : (
        <span className="block text-zinc-500">
          No Active or Not Qualified leads are currently assigned to them, so nothing will move to
          the Unassigned queue.
        </span>
      )}
    </span>
  );

  return (
    <UzConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title(userName)}
      description={description}
      confirmLabel={copy.confirmLabel}
      tone="destructive"
      onConfirm={onConfirm}
    />
  );
};
